import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import Button from "../../../../componentes/button/button";
import FilialService from "../../../../services/resource/filialService";
import TipoProdutoService from "../../../../services/resource/tipoProdutoService";
import SolicitacaoCompraService from "../../../../services/resource/solicitacaoCompraService";
import SolicitacaoComprasCampos, { calcularExigeVeiculo, validarCamposCompras } from "./SolicitacaoComprasCampos";

const filialService = new FilialService();
const tipoProdutoService = new TipoProdutoService();
const service = new SolicitacaoCompraService();

// Correlaciona as cotações prévias (por id da linha, que deixa de existir quando os produtos são
// reconstruídos na edição) para o id do produto no catálogo — mesma convenção usada na criação
// (ver SolicitacaoComprasCampos#produtosParaCotacao).
function remapearFornecedoresParaCatalogo(fornecedores, produtos) {
    const rowIdParaCatalogoId = Object.fromEntries(produtos.map(p => [p.id, p.produtoCatalogoId]));
    return (fornecedores || []).map(f => ({
        nome: f.nome,
        produtosCotados: f.produtosCotados.map(pc => ({
            produtoId: rowIdParaCatalogoId[pc.produtoId] ?? pc.produtoId,
            quantidade: pc.quantidade,
            valorUnitario: pc.valorUnitario,
            prazoEntrega: pc.prazoEntrega,
        })),
    }));
}

function montarFormInicial(detalheProcesso) {
    return {
        filialId: detalheProcesso.filialId ?? null,
        prioridade: detalheProcesso.prioridade ?? null,
        tipoProdutoId: detalheProcesso.tipoProdutoId ?? null,
        motivo: detalheProcesso.motivo ?? "",
        valorTotalEstimado: detalheProcesso.valorTotalEstimado ?? "",
        necessitaCotacao: detalheProcesso.necessitaCotacao ?? true,
        produtos: (detalheProcesso.produtos || []).map(p => ({
            produtoCatalogoId: p.produtoCatalogoId,
            produtoCatalogoNome: p.nome,
            unidadeMedida: p.unidadeMedida,
            quantidade: p.quantidade,
            detalhes: p.detalhes,
            placaVeiculo: p.placaVeiculo,
            veiculoLabel: p.placaVeiculo,
            anexo: null,
        })),
    };
}

function montarCotacaoInicial(detalheProcesso) {
    return {
        filialEntregaId: detalheProcesso.filialEntregaId ?? null,
        fornecedores: remapearFornecedoresParaCatalogo(detalheProcesso.fornecedores, detalheProcesso.produtos || []),
        observacao: detalheProcesso.observacaoCotacao ?? "",
        valorFinalCotacao: detalheProcesso.valorFinalCotacao ?? "",
    };
}

/**
 * Etapa 1 revisitada após um RETIFICAR (ver EtapaCompraAprovacaoSolicitacaoHandler no backend) —
 * ao contrário de EtapaSolicitacaoRetificada.jsx (placeholder genérico usado pelos demais
 * processos), aqui os campos originais ficam de fato editáveis, pré-preenchidos com os dados
 * atuais da solicitação (detalheProcesso, já carregado pela tela — sem chamada extra). Mesmos
 * campos/validação da criação (SolicitacaoComprasCampos), só muda o destino do envio: edita a
 * solicitação existente (PUT) em vez de criar uma nova (POST).
 */
export default function EtapaCompraSolicitacaoRetificada({ solicitacao, detalheProcesso, acaoEmExecucao, onAcaoConcluida, encaminhar, anexoObrigatorioNaEtapa, temAnexo, anexar }) {
    // `detalheProcesso` chega null no primeiro render da tela de detalhe (ela busca a solicitação
    // e só depois o detalhe do processo, em duas etapas) — não dá pra inicializar o form a partir
    // dele direto no useState (só rodaria uma vez, com null). Preenche via efeito assim que chega.
    const [form, setForm] = useState(null);
    const [cotacao, setCotacao] = useState(null);
    const [rateio, setRateio] = useState(null);
    const [filiais, setFiliais] = useState([]);
    const [tiposProduto, setTiposProduto] = useState([]);
    const [enviando, setEnviando] = useState(false);

    useEffect(() => {
        filialService.listarAtivas().then(res => setFiliais(res.data)).catch(() => setFiliais([]));
        tipoProdutoService.listarAtivos().then(res => setTiposProduto(res.data)).catch(() => setTiposProduto([]));
    }, []);

    useEffect(() => {
        if (!detalheProcesso) return;
        setForm(montarFormInicial(detalheProcesso));
        setCotacao(montarCotacaoInicial(detalheProcesso));
        setRateio((detalheProcesso.rateioOrcamentarioInicial || []).map(item => ({ ...item })));
    }, [detalheProcesso]);

    async function reenviar() {
        const exigeVeiculo = calcularExigeVeiculo(form, tiposProduto);
        const erro = validarCamposCompras({ form, cotacao, rateio, exigeVeiculo });
        if (erro) {
            toast.error(erro);
            return;
        }
        if (anexoObrigatorioNaEtapa && !temAnexo) {
            toast.error("É necessário anexar pelo menos um documento para concluir esta etapa.");
            return;
        }

        // Diferente da criação (onde `encaminhar` espera o código da ETAPA DESTINO, já que a
        // solicitação ainda não existe), aqui — dentro da tela de detalhe — `encaminhar` espera o
        // código da AÇÃO válida na etapa atual (ETAPA_1_SOLICITACAO só permite "ENVIAR"; o próprio
        // backend decide o destino real — Etapa 2 ou Etapa 4, conforme necessitaCotacao — ao
        // executar essa ação, tanto na prévia quanto no envio real).
        let usuarioAprovadorId;
        try {
            usuarioAprovadorId = await encaminhar("ENVIAR");
        } catch {
            return;
        }

        const anexosProdutosNovos = form.produtos.filter(p => p.anexo instanceof File);

        const dadosCotacao = form.necessitaCotacao === false ? {
            filialEntregaId: cotacao.filialEntregaId,
            fornecedores: cotacao.fornecedores.map(f => ({
                nome: f.nome,
                produtosCotados: f.produtosCotados.map(l => ({
                    produtoId: l.produtoId,
                    quantidade: Number(l.quantidade),
                    valorUnitario: Number(l.valorUnitario),
                    prazoEntrega: l.prazoEntrega,
                })),
            })),
            observacao: cotacao.observacao,
            valorFinalCompra: Number(cotacao.valorFinalCotacao),
        } : {};

        setEnviando(true);
        service.editarSolicitacao(solicitacao.id, {
            ...form,
            ...dadosCotacao,
            usuarioAprovadorId,
            produtos: form.produtos.map(p => ({
                produtoCatalogoId: p.produtoCatalogoId,
                quantidade: Number(p.quantidade),
                detalhes: p.detalhes,
                placaVeiculo: p.placaVeiculo,
            })),
            rateioOrcamentario: rateio.map(item => ({ baseBpId: item.baseBpId })),
        })
            .then(async res => {
                for (const produto of anexosProdutosNovos) {
                    try {
                        await anexar(produto.anexo, `Produto: ${produto.produtoCatalogoNome || "produto"}`);
                    } catch (error) {
                        console.error("Erro ao anexar arquivo do produto", error);
                        toast.error(`Erro ao anexar "${produto.anexo.name}"`);
                    }
                }
                onAcaoConcluida("ENVIAR", res.data);
            })
            .catch(error => console.error("Erro ao reenviar solicitação", error))
            .finally(() => setEnviando(false));
    }

    if (!form || !cotacao || !rateio) {
        return (
            <div className="flex items-center justify-center py-8 text-sm text-muted-foreground">
                <i className="fa fa-spinner fa-spin mr-2" /> Carregando dados da solicitação...
            </div>
        );
    }

    return (
        <div className="flex flex-col gap-4">
            <p className="text-sm text-muted-foreground">
                Esta solicitação foi retificada e voltou para a etapa inicial. Ajuste os campos abaixo
                conforme solicitado e reenvie.
            </p>

            <SolicitacaoComprasCampos
                form={form}
                setForm={setForm}
                cotacao={cotacao}
                setCotacao={setCotacao}
                rateio={rateio}
                setRateio={setRateio}
                filiais={filiais}
                tiposProduto={tiposProduto}
            />

            <div className="flex justify-end">
                <Button
                    variant="blue"
                    label="Reenviar"
                    iconLeft="fa fa-paper-plane"
                    loading={enviando || acaoEmExecucao === "ENVIAR"}
                    onClick={reenviar}
                />
            </div>
        </div>
    );
}
